import { EyeIcon, InfoIcon, TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { FormWrapper } from '@/form-control'
import { TextAreaField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { IChatPromptSettings } from '@/MODELS/chatPromptSettings.model'

import { LoadingBoundary, useResource } from '@/services/resource.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Button from '@/components/Button'
import Loading from '@/components/Loading'

export default function AdminChatPromptForm() {
    const navigate = useNavigate()
    const [chatPrompts, setChatPrompts] = useResource<IChatPromptSettings[]>(ROUTES.admin.chatPrompt)
    const [chatPromptId, setChatPromptId] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [activeTab, setActiveTab] = useState<'base' | 'task' | 'compassionate' | 'knowledge' | 'safety'>('base')

    const openPreviewModal = () => {
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
    }

    const generateCompletePrompt = (prompts: IChatPromptSettings) => {
        if (!prompts) return 'No prompt data available'

        let completePrompt = '=== COMPLETE AI PROMPT CONFIGURATION ===\n\n'

        completePrompt += '1. BASE PROMPT (Core Instructions)\n'
        completePrompt += '─'.repeat(50) + '\n'
        completePrompt += prompts.basePrompt || '[Base prompt not set]'
        completePrompt += '\n\n'

        completePrompt += '2. TASK PROMPT (Specific Objectives)\n'
        completePrompt += '─'.repeat(50) + '\n'
        completePrompt += prompts.taskPrompt || '[Task prompt not set]'
        completePrompt += '\n\n'

        completePrompt += '4. KNOWLEDGE BASE PROMPT (Context Instructions)\n'
        completePrompt += '─'.repeat(50) + '\n'
        completePrompt += prompts.knowledgeBasePrompt || '[Knowledge base prompt not set]'
        completePrompt += '\n\n'

        completePrompt += '5. SAFETY GUIDELINES PROMPT (Boundaries & Ethics)\n'
        completePrompt += '─'.repeat(50) + '\n'
        completePrompt += prompts.safetyGuidelinesPrompt || '[Safety guidelines not set]'

        return completePrompt
    }

    useEffect(() => {
        const fetchChatPrompts = async () => {
            try {
                setLoading(true)
                if (chatPrompts && chatPrompts.length > 0) {
                    setChatPromptId(chatPrompts[0]._id)
                }
            } catch (error) {
                console.error('Error fetching chat prompts:', error)
                setChatPromptId('new')
            } finally {
                setLoading(false)
            }
        }

        fetchChatPrompts()
    }, [chatPrompts])

    if (loading) return <Loading />

    const promptDescriptions = {
        base: {
            title: 'Base Prompt',
            description:
                'Core instructions that define who the AI is, its personality, and communication style. This sets the foundation for all interactions.',
            example: 'Example: "You are a helpful assistant with expertise in..."',
        },
        task: {
            title: 'Task Prompt',
            description:
                'Specific objectives and tasks the AI should accomplish. This prompt can include user-specific information using placeholders.',
            example:
                'Example: "Your task is to provide support to {{userInfo.guardianRole}} about {{userInfo.childName}}..."',
        },

        knowledge: {
            title: 'Knowledge Base Prompt',
            description:
                'Instructions for how the AI should use and reference the knowledge base. This controls how context is integrated.',
            example: 'Example: "Use the following knowledge base to answer questions..."',
        },
        safety: {
            title: 'Safety Guidelines',
            description:
                'Boundaries, ethical guidelines, and safety protocols. Define what the AI should avoid and how to handle sensitive topics.',
            example: 'Example: "Never provide medical advice. Always redirect to..."',
        },
    }

    const userInfoPlaceholders = [
        { placeholder: '{{userInfo.childName}}', description: "The child's name" },
        { placeholder: '{{userInfo.childAge}}', description: "The child's age" },
        { placeholder: '{{userInfo.guardianRole}}', description: "The guardian's role (parent, caregiver, etc.)" },
        { placeholder: '{{userInfo.mainConcerns}}', description: 'Array of main concerns (joined with commas)' },
        { placeholder: '{{userInfo.currentDiagnoseStep}}', description: 'Current diagnosis journey step' },
        { placeholder: '{{userInfo.communicationLevel}}', description: "The child's communication level" },
        { placeholder: '{{userInfo.supportNeeds}}', description: "The child's daily support needs" },
    ]

    return (
        <LoadingBoundary>
            <FormWrapper<IChatPromptSettings>
                endpoint={ROUTES.admin.chatPrompt}
                submitButtonText={'Save All Prompts'}
                id={chatPromptId}
                callbackAfterSubmit={() => {
                    navigate('/admin/chat-prompt-settings')
                }}
                className='p-6 sm:p-10'
                submitButtonClass={'bg-brand-600'}
            >
                {(f, { formValues, setFormValues }) => {
                    return (
                        <div className='space-y-6'>
                            <AdminPageHeader
                                heading={'AI Chat Configuration'}
                                subheading="Configure the AI assistant's behavior, knowledge, and boundaries"
                                className='py-5 bg-white rounded-md'
                                action={
                                    <Button.Icon
                                        className='text-gray-600'
                                        icon={<EyeIcon />}
                                        onClick={openPreviewModal}
                                    >
                                        Preview Complete Prompt
                                    </Button.Icon>
                                }
                            />

                            <div className='bg-white rounded-xl shadow-sm border border-neutral-200 p-2'>
                                <div className='flex gap-2 flex-wrap'>
                                    {(['base', 'task', 'knowledge', 'safety'] as const).map((tab) => (
                                        <button
                                            key={tab}
                                            type='button'
                                            onClick={() => setActiveTab(tab)}
                                            className={`flex-1 min-w-[120px] px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                                                activeTab === tab
                                                    ? 'bg-brand-100 border border-brand-200 text-gray-900'
                                                    : 'bg-neutral-50 text-neutral-700 hover:bg-neutral-100'
                                            }`}
                                        >
                                            <div className='flex items-center justify-center gap-2'>
                                                <span className='text-sm sm:text-base'>
                                                    {promptDescriptions[tab].title}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className='bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden'>
                                <div className='px-6 py-4 border-b border-gray-200'>
                                    <div className='flex items-start gap-3'>
                                        <div className='w-full'>
                                            <h2 className='text-xl font-semibold text-gray-900'>
                                                {promptDescriptions[activeTab].title}
                                            </h2>
                                            <p className='text-gray-700 mt-1'>
                                                {promptDescriptions[activeTab].description}
                                            </p>
                                            <p className='text-sm text-secondary-600 mt-2 italic'>
                                                {promptDescriptions[activeTab].example}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className='p-6'>
                                    {activeTab === 'base' && (
                                        <TextAreaField
                                            rows={12}
                                            value={formValues.basePrompt || ''}
                                            onChange={(value) => setFormValues({ ...formValues, basePrompt: value })}
                                            label={'Base Instructions'}
                                            placeholder='Define the AI assistant personality, tone, and core behavior...'
                                            required
                                            className='font-mono text-sm'
                                        />
                                    )}

                                    {activeTab === 'task' && (
                                        <>
                                            <TextAreaField
                                                rows={12}
                                                value={formValues.taskPrompt || ''}
                                                onChange={(value) =>
                                                    setFormValues({ ...formValues, taskPrompt: value })
                                                }
                                                label={'Task Instructions'}
                                                placeholder='Define specific tasks and objectives for the AI...'
                                                required
                                                className='font-mono text-sm'
                                            />
                                            <div className='mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4'>
                                                <div className='flex items-start gap-3'>
                                                    <InfoIcon className='w-5 h-5 text-brand-600 mt-0.5 flex-shrink-0' />
                                                    <div className='text-sm text-brand-800'>
                                                        <p className='font-semibold mb-2'>
                                                            Available User Info Placeholders:
                                                        </p>
                                                        <div className='space-y-1'>
                                                            {userInfoPlaceholders.map((item) => (
                                                                <div
                                                                    key={item.placeholder}
                                                                    className='flex items-start'
                                                                >
                                                                    <code className='bg-blue-100 px-1.5 py-0.5 rounded text-brand-900 font-mono mr-2'>
                                                                        {item.placeholder}
                                                                    </code>
                                                                    <span className='text-brand-700'>
                                                                        - {item.description}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <p className='mt-2 text-brand-700'>
                                                            These placeholders will be automatically replaced with
                                                            actual user information when the chat starts.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {activeTab === 'knowledge' && (
                                        <>
                                            <TextAreaField
                                                rows={12}
                                                value={formValues.knowledgeBasePrompt || ''}
                                                onChange={(value) =>
                                                    setFormValues({ ...formValues, knowledgeBasePrompt: value })
                                                }
                                                label={'Knowledge Base Instructions'}
                                                placeholder='Instructions for how to use and reference the knowledge base...'
                                                required
                                                className='font-mono text-sm'
                                            />
                                            <div className='mt-4 bg-secondary-50 border border-secondary-200 rounded-lg p-4'>
                                                <div className='flex items-start gap-3'>
                                                    <TriangleAlert className='w-5 h-5 text-secondary-600 mt-0.5 flex-shrink-0' />
                                                    <div className='text-sm text-secondary-800'>
                                                        <p className='font-semibold mb-1'>
                                                            {`Important: Keep the {{ KNOWLEDGE_BASE }} placeholder!}`}
                                                        </p>
                                                        <p>
                                                            The{' '}
                                                            <code className='bg-secondary-100 px-1.5 py-0.5 rounded text-secondary-900 font-mono'>
                                                                {'{{KNOWLEDGE_BASE}}'}
                                                            </code>{' '}
                                                            placeholder will be automatically replaced with the actual
                                                            knowledge content when the AI runs. Make sure to include it
                                                            in your prompt where you want the knowledge to be inserted.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {activeTab === 'safety' && (
                                        <TextAreaField
                                            rows={12}
                                            value={formValues.safetyGuidelinesPrompt || ''}
                                            onChange={(value) =>
                                                setFormValues({ ...formValues, safetyGuidelinesPrompt: value })
                                            }
                                            label={'Safety Guidelines'}
                                            placeholder='Define boundaries, ethical guidelines, and safety protocols...'
                                            required
                                            className='font-mono text-sm'
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                }}
            </FormWrapper>

            {isModalOpen && (
                <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
                    <div className='bg-white rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl'>
                        <div className='flex items-center justify-between p-6 border-b border-neutral-200'>
                            <div>
                                <h3 className='text-2xl font-bold text-neutral-900'>Complete Prompt Preview</h3>
                                <p className='text-neutral-600 mt-1'>
                                    This is the complete configuration that will be sent to the AI
                                </p>
                            </div>
                            <button
                                onClick={closeModal}
                                className='text-neutral-400 hover:text-neutral-600 transition-colors'
                            >
                                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M6 18L18 6M6 6l12 12'
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className='flex-1 overflow-auto p-6'>
                            <pre className='bg-neutral-50 border border-neutral-200 rounded-lg p-6 text-sm font-mono overflow-auto whitespace-pre-wrap text-neutral-700'>
                                {chatPrompts && chatPrompts[0] && generateCompletePrompt(chatPrompts[0])}
                            </pre>
                        </div>

                        <div className='p-6 flex w-full justify-end border-t border-neutral-200'>
                            <Button onClick={closeModal}>Close Preview</Button>
                        </div>
                    </div>
                </div>
            )}
        </LoadingBoundary>
    )
}
